<script setup lang="ts">
import { ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import AppButton from '@uiKit/elements/Button.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'

const props = defineProps<{
  walletAddress: string
}>()

const emit = defineEmits<{
  (e: 'check-status'): void
}>()

const proceeded = ref(false)

const onProceed = () => {
  proceeded.value = true
}

const copyAddress = async () => {
  try {
    await navigator.clipboard.writeText(props.walletAddress)
  } catch {
    // clipboard access denied — no-op
  }
}
</script>

<template>
  <Box align="start" gap="6" class="twa">
    <!-- Left: warning + linked wallet info -->
    <Box direction="col" gap="3" grow class="twa__left">
      <Box align="start" gap="2" class="twa__warning">
        <Text is="span" class="twa__warning-icon">⚠️</Text>
        <Box direction="col" gap="1" class="twa__warning-body">
          <Text type="heading" is="span" class="twa__warning-title">
            {{ proceeded ? 'Wallet Not Active on Network' : 'Wallet Activation Required' }}
          </Text>
          <Text type="caption" weight="medium" color="muted" class="twa__warning-desc">
            <template v-if="proceeded">
              Your wallet address is linked to your Sliceal account but has not been activated on
              the XRP Ledger. To activate it, you must deposit the minimum required balance into
              your wallet.
            </template>
            <template v-else>
              Your wallet address is linked but not yet active on the network. You must deposit the
              minimum required balance to activate it.
            </template>
          </Text>
        </Box>
      </Box>

      <Box align="center" gap="2" class="twa__linked">
        <Text is="span" class="twa__linked-icon">🔗</Text>
        <Box direction="col" gap="1" grow class="twa__linked-body">
          <Text type="heading" is="span" class="twa__linked-label">Linked Wallet</Text>
          <Text type="body" color="muted" is="span" class="twa__linked-address">
            {{ walletAddress }}
          </Text>
        </Box>
        <Box
          is="button"
          flex
          align="center"
          class="twa__copy-btn"
          type="button"
          aria-label="Copy wallet address"
          @click="copyAddress"
        >
          <PiIcon name="copy" />
        </Box>
      </Box>
    </Box>

    <!-- Right: steps + CTAs -->
    <Box direction="col" gap="2" grow class="twa__right">
      <Box direction="col" gap="3" class="twa__steps">
        <Box align="center" gap="3" class="twa__step">
          <Box flex align="center" justify="center" class="twa__step-badge twa__step-badge--done">
            <PiIcon name="check" class="twa__step-check" />
          </Box>
          <Box direction="col" gap="1" class="twa__step-info">
            <Text
              type="body"
              weight="semibold"
              is="span"
              class="twa__step-title twa__step-title--done"
            >
              Wallet Connected
            </Text>
            <Text type="caption" weight="medium" color="muted" is="span">
              Wallet address linked to your account.
            </Text>
          </Box>
        </Box>

        <Box align="center" gap="3" class="twa__step twa__step--active">
          <Text
            type="caption"
            weight="medium"
            align="center"
            is="div"
            class="twa__step-badge twa__step-badge--active"
          >
            2
          </Text>
          <Box direction="col" gap="1" class="twa__step-info">
            <Text
              type="body"
              weight="semibold"
              is="span"
              class="twa__step-title twa__step-title--active"
            >
              Activate Wallet
            </Text>
            <Text type="caption" weight="medium" color="muted" is="span">
              Ensure wallet is active on the XRP Ledger
            </Text>
          </Box>
        </Box>

        <Box align="center" gap="3" class="twa__step">
          <Text
            type="caption"
            weight="medium"
            align="center"
            is="div"
            class="twa__step-badge twa__step-badge--pending"
          >
            3
          </Text>
          <Box direction="col" gap="1" class="twa__step-info">
            <Text type="body" weight="semibold" is="span" class="twa__step-title">
              Enable Trustline
            </Text>
            <Text type="caption" weight="medium" color="muted" is="span">
              Set up trustline to receive ARVA property tokens.
            </Text>
          </Box>
        </Box>
      </Box>

      <Box direction="col" gap="3" class="twa__actions">
        <template v-if="proceeded">
          <AppButton
            label="Check Activation Status"
            size="large"
            block
            icon="pi pi-arrow-right"
            @click="emit('check-status')"
          />
          <AppButton
            label="Copy Wallet Address"
            size="large"
            variant="outline"
            block
            @click="copyAddress"
          />
        </template>
        <AppButton
          v-else
          label="Proceed to Activate Wallet"
          size="large"
          block
          @click="onProceed"
        />
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.twa {
  width: 100%;
}

.twa__warning {
  background: var(--p-warning-50);
  border: 1px solid var(--p-warning-400);
  border-radius: var(--p-border-radius-lg);
  padding: 19px 21px;
}

.twa__warning-icon {
  font-size: 18px;
  line-height: 24px;
  flex-shrink: 0;
}

.twa__warning-title {
  color: var(--p-warning-700);
  line-height: 24px;
}

.twa__warning-desc {
  line-height: 18px;
  margin: 0;
}

.twa__linked {
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
  padding: 19px 21px;
}

.twa__linked-icon {
  font-size: 16px;
  line-height: 24px;
  flex-shrink: 0;
}

.twa__linked-body {
  min-width: 0;
}

.twa__linked-label {
  line-height: 24px;
}

.twa__linked-address {
  line-height: 20px;
  word-break: break-all;
}

.twa__copy-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--p-text-muted-color);
  padding: 4px;
  flex-shrink: 0;
  transition: color 0.15s ease;
}

.twa__copy-btn :deep(.pi) {
  font-size: 16px;
}

.twa__copy-btn:hover {
  color: var(--p-text-color);
}

.twa__copy-btn:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 2px;
}

.twa__steps {
  background: var(--p-surface-0);
  border-radius: var(--p-border-radius-lg);
  padding: 1.25rem;
}

.twa__step {
  background: var(--p-surface-0);
  border: 1px solid var(--p-surface-100);
  border-radius: var(--p-border-radius-lg);
  padding: 18px 1.25rem;
}

.twa__step--active {
  border-color: var(--p-warning-400);
  box-shadow:
    0px 1px 3px 0px rgba(10, 13, 18, 0.1),
    0px 1px 2px 0px rgba(10, 13, 18, 0.06);
}

.twa__step-badge {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  flex-shrink: 0;
}

.twa__step-badge--done {
  background: var(--p-success-50);
  border: 1px solid var(--p-success-500);
}

.twa__step-check {
  font-size: 13px;
  color: var(--p-success-700);
}

.twa__step-badge--active {
  background: var(--p-warning-50);
  border: 1px solid var(--p-warning-400);
  color: var(--p-text-muted-color);
}

.twa__step-badge--pending {
  background: var(--p-surface-100);
  border: 1px solid var(--p-surface-300);
  color: var(--p-text-muted-color);
}

.twa__step-title {
  line-height: 20px;
}

.twa__step-title--done {
  color: var(--p-success-700);
}

.twa__step-title--active {
  color: var(--p-warning-600);
}

@media (max-width: 992px) {
  .twa {
    flex-direction: column;
    gap: 1.25rem;
  }
}
</style>
